import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BarChart3, TrendingUp, TrendingDown, Users, Building2, CreditCard,
  Tag, Calendar, RefreshCw, DollarSign, Percent, Activity, Download,
  Globe, MapPin, ChevronRight, Layout, Clock, Mail
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeAnalytics() {
  const { theme } = useOutletContext();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [stats, setStats] = useState(null);
  const [detailed, setDetailed] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30d");
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => { fetchAnalytics(); }, [timeRange]);
const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, promoRes, detailedRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/super-admin/promo-codes`, { withCredentials: true }).catch(() => ({ data: { promo_codes: [] } })),
        axios.get(`${API}/super-admin/analytics/detailed`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      if (statsRes.data) setStats(statsRes.data);
      if (detailedRes.data) setDetailed(detailedRes.data);
      setPromoCodes(promoRes.data.promo_codes || []);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);
const fmt = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No expiry';
  const fmtNum = (n) => (n || 0).toLocaleString();

  const exportCSV = () => {
    if (!detailed?.companies) return;
    const headers = ['Company','Customer ID','Plan','Status','Users','Layouts','Created','Phone'];
    const rows = detailed.companies.map(c => [c.name, c.customer_id || '-', c.plan, c.status, c.users, c.layouts, fmtDate(c.created_at), c.phone || '-']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `seatpilot-backoffice-analytics-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const promoAnalytics = {
    totalCodes: promoCodes.length,
    activeCodes: promoCodes.filter(p => p.is_active).length,
    totalUses: promoCodes.reduce((sum, p) => sum + (p.times_used || 0), 0),
    totalDiscount: promoCodes.reduce((sum, p) => sum + ((p.times_used || 0) * (p.discount_percent || 0) / 100 * 50), 0)
  };

  const companies = detailed?.companies || [];
  const users = detailed?.users || [];
  const geo = detailed?.geo_distribution || [];
  const signupTrend = detailed?.signup_trend || [];

  const planDist = (() => {
    const map = {};
    companies.forEach(c => { const p = c.plan || 'none'; map[p] = (map[p] || 0) + 1; });
    const colors = ['#8b5cf6','#3b82f6','#10b981','#f59e0b','#ef4444','#6366f1','#ec4899'];
    return Object.entries(map).map(([name, value], i) => ({ name, value, color: colors[i % colors.length] }));
  })();

  const cn = (base, dark) => isDark ? dark : base;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${cn('text-gray-900', 'text-white')}`}>Analytics</h1>
          <p className={`mt-1 ${cn('text-gray-500', 'text-slate-400')}`}>Platform performance and metrics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {timeRange === 'custom' && (
            <div className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="text-xs border rounded px-2 py-1.5" />
              <span className={`text-xs ${cn('text-gray-400', 'text-slate-500')}`}>to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="text-xs border rounded px-2 py-1.5" />
            </div>
          )}
          <Button onClick={exportCSV} variant="outline" size="sm" className="gap-2">
            <Download className="w-4 h-4" /> Export
          </Button>
          <Button onClick={fetchAnalytics} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-violet-500" /></div>
      ) : (
        <>
          {/* ===== ORIGINAL SECTION: Revenue & Subscription KPIs ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className={`p-5 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${cn('text-gray-500', 'text-slate-400')}`}>Monthly Recurring Revenue</p>
                  <p className={`text-2xl font-bold mt-1 ${cn('text-gray-900', 'text-white')}`}>{fmt(stats?.mrr)}</p>
                </div>
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center"><DollarSign className="w-6 h-6 text-green-500" /></div>
              </div>
              <div className="mt-3 flex items-center text-sm"><TrendingUp className="w-4 h-4 text-green-500 mr-1" /><span className="text-green-500">+12%</span><span className={`ml-1 ${cn('text-gray-400', 'text-slate-500')}`}>vs last month</span></div>
            </Card>
            <Card className={`p-5 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${cn('text-gray-500', 'text-slate-400')}`}>Total Revenue</p>
                  <p className={`text-2xl font-bold mt-1 ${cn('text-gray-900', 'text-white')}`}>{fmt(stats?.total_revenue)}</p>
                </div>
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center"><CreditCard className="w-6 h-6 text-violet-500" /></div>
              </div>
              <div className="mt-3 text-sm"><span className={cn('text-gray-500', 'text-slate-400')}>Lifetime value</span></div>
            </Card>
            <Card className={`p-5 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${cn('text-gray-500', 'text-slate-400')}`}>Active Subscriptions</p>
                  <p className={`text-2xl font-bold mt-1 ${cn('text-gray-900', 'text-white')}`}>{stats?.active_subscriptions || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center"><Activity className="w-6 h-6 text-blue-500" /></div>
              </div>
              <div className="mt-3 text-sm"><span className={cn('text-gray-500', 'text-slate-400')}>Of {stats?.total_companies || 0} companies</span></div>
            </Card>
            <Card className={`p-5 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${cn('text-gray-500', 'text-slate-400')}`}>Churn Rate</p>
                  <p className={`text-2xl font-bold mt-1 ${cn('text-gray-900', 'text-white')}`}>{stats?.churn_rate || 2.5}%</p>
                </div>
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center"><TrendingDown className="w-6 h-6 text-red-500" /></div>
              </div>
              <div className="mt-3 text-sm"><span className={cn('text-gray-500', 'text-slate-400')}>Monthly average</span></div>
            </Card>
          </div>

          {/* ===== ORIGINAL SECTION: User & Company Metrics ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className={`p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <h3 className={`text-lg font-semibold mb-4 ${cn('text-gray-900', 'text-white')}`}>User Metrics</h3>
              <div className="space-y-4">
                {[
                  { icon: Users, color: 'blue', label: 'Total Users', value: users.length || stats?.total_users || 0 },
                  { icon: Activity, color: 'green', label: 'Active Today', value: stats?.active_users_today || 0 },
                  { icon: Calendar, color: 'violet', label: 'New This Month', value: stats?.new_users_this_month || 0 }
                ].map((m, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${cn('bg-gray-50', 'bg-slate-700/50')}`}>
                    <div className="flex items-center gap-3">
                      <m.icon className={`w-5 h-5 text-${m.color}-${isDark ? '400' : '600'}`} />
                      <span className={cn('text-gray-700', 'text-slate-300')}>{m.label}</span>
                    </div>
                    <span className={`font-bold ${cn('text-gray-900', 'text-white')}`}>{m.value}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className={`p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <h3 className={`text-lg font-semibold mb-4 ${cn('text-gray-900', 'text-white')}`}>Company Metrics</h3>
              <div className="space-y-4">
                {[
                  { icon: Building2, color: 'blue', label: 'Total Companies', value: companies.length || stats?.total_companies || 0 },
                  { icon: TrendingUp, color: 'green', label: 'New This Month', value: stats?.new_companies_this_month || 0 },
                  { icon: BarChart3, color: 'amber', label: 'Avg Users per Company', value: companies.length ? Math.round(users.length / companies.length) : 0 }
                ].map((m, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${cn('bg-gray-50', 'bg-slate-700/50')}`}>
                    <div className="flex items-center gap-3">
                      <m.icon className={`w-5 h-5 text-${m.color}-${isDark ? '400' : '600'}`} />
                      <span className={cn('text-gray-700', 'text-slate-300')}>{m.label}</span>
                    </div>
                    <span className={`font-bold ${cn('text-gray-900', 'text-white')}`}>{m.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* ===== NEW: Charts Row ===== */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className={`p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${cn('text-gray-900', 'text-white')}`}>
                <TrendingUp className="w-4 h-4 text-green-500" /> User Signups (12 weeks)
              </h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={signupTrend}>
                    <defs><linearGradient id="colorSignups" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#f0f0f0'} />
                    <XAxis dataKey="date" tick={{fontSize: 10, fill: isDark ? '#94a3b8' : '#9ca3af'}} />
                    <YAxis tick={{fontSize: 10, fill: isDark ? '#94a3b8' : '#9ca3af'}} allowDecimals={false} />
                    <Tooltip contentStyle={{fontSize: 12, borderRadius: 8, background: isDark ? '#1e293b' : '#fff'}} />
                    <Area type="monotone" dataKey="signups" stroke="#10b981" fill="url(#colorSignups)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className={`p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${cn('text-gray-900', 'text-white')}`}>
                <BarChart3 className="w-4 h-4 text-violet-500" /> Plan Distribution
              </h3>
              {planDist.length > 0 ? (
                <div className="h-48 flex items-center">
                  <ResponsiveContainer width="55%" height="100%">
                    <PieChart><Pie data={planDist} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" stroke="none">{planDist.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip contentStyle={{fontSize: 12, borderRadius: 8}} /></PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2 pl-2">
                    {planDist.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{background: p.color}} />
                        <span className={`${cn('text-gray-700', 'text-slate-300')} truncate flex-1`}>{p.name}</span>
                        <span className={`font-semibold ${cn('text-gray-900', 'text-white')}`}>{p.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>}
            </Card>
          </div>

          {/* ===== NEW: Geo + Companies ===== */}
          <div className="grid lg:grid-cols-5 gap-6">
            <Card className={`lg:col-span-2 p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${cn('text-gray-900', 'text-white')}`}>
                <Globe className="w-4 h-4 text-blue-500" /> Login Geography
              </h3>
              {geo.length > 0 ? (
                <div className="space-y-2">
                  {geo.slice(0, 8).map((g, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${cn('hover:bg-gray-50', 'hover:bg-slate-700/50')} transition-colors`}>
                      <MapPin className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className={`flex-1 text-sm ${cn('text-gray-700', 'text-slate-300')}`}>{g.country}</span>
                      <span className={`text-xs ${cn('text-gray-400', 'text-slate-500')}`}>{g.cities} cities</span>
                      <span className={`text-sm font-semibold ${cn('text-gray-900', 'text-white')}`}>{g.logins} logins</span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-center py-8 text-gray-400"><Globe className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-xs">No login data yet</p></div>}
              <p className={`text-xs mt-3 ${cn('text-gray-400', 'text-slate-500')}`}>Last 30 days | {detailed?.total_logins_30d || 0} total logins</p>
            </Card>
            <Card className={`lg:col-span-3 p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
              <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${cn('text-gray-900', 'text-white')}`}>
                <Building2 className="w-4 h-4 text-violet-500" /> Companies ({companies.length})
              </h3>
              <div className="space-y-2">
                {companies.map((co, i) => (
                  <div key={co.company_id || i} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${cn('border-gray-100 hover:border-violet-200 hover:bg-violet-50/20', 'border-slate-700 hover:border-violet-500/50 hover:bg-slate-700/50')}`} onClick={() => navigate(`/backoffice/customers/${co.company_id}`)}>
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-xs font-bold text-white shrink-0">{(co.name || '?')[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${cn('text-gray-800', 'text-white')}`}>{co.name}</p>
                      <p className={`text-[10px] ${cn('text-gray-400', 'text-slate-500')}`}>{co.customer_id || co.company_id} | {fmtDate(co.created_at)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`px-2 py-0.5 text-[10px] rounded font-medium ${co.status === 'active' || co.status === 'trial' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{co.plan}</span>
                      <p className={`text-[10px] mt-0.5 ${cn('text-gray-400', 'text-slate-500')}`}>{co.users} users | {co.layouts} layouts</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${cn('text-gray-300', 'text-slate-600')}`} />
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* ===== NEW: All Users Table ===== */}
          <Card className={`p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${cn('text-gray-900', 'text-white')}`}>
              <Users className="w-4 h-4 text-blue-500" /> All Users ({users.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className={`border-b ${cn('border-gray-200', 'border-slate-700')}`}>
                  {['Name','Email','Company','Joined','Last Login'].map(h => <th key={h} className={`text-left py-2 px-3 text-xs font-medium ${cn('text-gray-500', 'text-slate-400')}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {users.map((u, i) => {
                    const co = companies.find(c => c.company_id === u.company_id);
                    return (
                      <tr key={u.user_id || i} className={`border-b cursor-pointer ${cn('border-gray-100 hover:bg-gray-50', 'border-slate-700 hover:bg-slate-700/50')}`} onClick={() => co && navigate(`/backoffice/customers/${co.company_id}`)}>
                        <td className={`py-2.5 px-3 font-medium ${cn('text-gray-800', 'text-white')}`}>{u.name || '-'}</td>
                        <td className={`py-2.5 px-3 ${cn('text-gray-600', 'text-slate-300')}`}>{u.email}</td>
                        <td className={`py-2.5 px-3 ${cn('text-gray-500', 'text-slate-400')}`}>{co?.name || u.company_id}</td>
                        <td className={`py-2.5 px-3 text-xs ${cn('text-gray-400', 'text-slate-500')}`}>{fmtDate(u.created_at)}</td>
                        <td className={`py-2.5 px-3 text-xs ${cn('text-gray-400', 'text-slate-500')}`}>{fmtDate(u.last_login)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ===== ORIGINAL SECTION: Promo Code Analytics (Full version) ===== */}
          <Card className={`p-6 ${cn('', 'bg-slate-800 border-slate-700')}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-semibold ${cn('text-gray-900', 'text-white')}`}>Promo Code Analytics</h3>
              <Tag className={`w-5 h-5 ${cn('text-violet-600', 'text-violet-400')}`} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${cn('bg-gray-50', 'bg-slate-700/50')}`}>
                <p className={`text-xs ${cn('text-gray-500', 'text-slate-400')}`}>Total Codes</p>
                <p className={`text-xl font-bold ${cn('text-gray-900', 'text-white')}`}>{promoAnalytics.totalCodes}</p>
              </div>
              <div className={`p-4 rounded-lg ${cn('bg-gray-50', 'bg-slate-700/50')}`}>
                <p className={`text-xs ${cn('text-gray-500', 'text-slate-400')}`}>Active Codes</p>
                <p className="text-xl font-bold text-green-500">{promoAnalytics.activeCodes}</p>
              </div>
              <div className={`p-4 rounded-lg ${cn('bg-gray-50', 'bg-slate-700/50')}`}>
                <p className={`text-xs ${cn('text-gray-500', 'text-slate-400')}`}>Total Uses</p>
                <p className={`text-xl font-bold ${cn('text-gray-900', 'text-white')}`}>{promoAnalytics.totalUses}</p>
              </div>
              <div className={`p-4 rounded-lg ${cn('bg-gray-50', 'bg-slate-700/50')}`}>
                <p className={`text-xs ${cn('text-gray-500', 'text-slate-400')}`}>Est. Discount Given</p>
                <p className={`text-xl font-bold ${cn('text-gray-900', 'text-white')}`}>{fmt(promoAnalytics.totalDiscount)}</p>
              </div>
            </div>
            {promoCodes.length === 0 ? (
              <div className="text-center py-8">
                <Tag className={`w-12 h-12 mx-auto mb-3 ${cn('text-gray-300', 'text-slate-600')}`} />
                <p className={cn('text-gray-500', 'text-slate-400')}>No promo codes yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className={`border-b ${cn('border-gray-200', 'border-slate-700')}`}>
                    {['Code','Discount','Uses','Max Uses','Expires','Status'].map(h => <th key={h} className={`text-left py-3 px-4 text-sm font-medium ${cn('text-gray-500', 'text-slate-400')}`}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {promoCodes.map(promo => (
                      <tr key={promo.promo_id} className={`border-b ${cn('border-gray-100 hover:bg-gray-50', 'border-slate-700 hover:bg-slate-700/50')}`}>
                        <td className={`py-3 px-4 font-mono font-bold ${cn('text-gray-900', 'text-white')}`}>{promo.code}</td>
                        <td className={`py-3 px-4 ${cn('text-gray-700', 'text-slate-300')}`}><span className="flex items-center gap-1"><Percent className="w-4 h-4" />{promo.discount_percent}%</span></td>
                        <td className={`py-3 px-4 font-bold ${cn('text-gray-900', 'text-white')}`}>{promo.times_used || 0}</td>
                        <td className={`py-3 px-4 ${cn('text-gray-500', 'text-slate-400')}`}>{promo.max_uses || 'Unlimited'}</td>
                        <td className={`py-3 px-4 ${cn('text-gray-500', 'text-slate-400')}`}>{fmtDate(promo.expires_at)}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs font-medium ${promo.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{promo.is_active ? 'Active' : 'Inactive'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
